import React, { useState, useEffect, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import logo from '../../assests/loginlogo.png';
import { getErrorMessage, showErrorToast, showSuccessToast } from '../../utils/toast';

interface LoginFormData {
  email: string;
  password: string;
}

type LoginFormErrors = Partial<Record<keyof LoginFormData, string>>;

interface LoginPageProps {
  setAuth: React.Dispatch<React.SetStateAction<boolean>>;
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const LoginPage: React.FC<LoginPageProps> = ({ setAuth }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });

  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [id]: '',
    }));
  };

  const validateForm = (): LoginFormErrors => {
    const errors: LoginFormErrors = {};
    if (!formData.email || !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email.';
    }
    if (!formData.password) {
      errors.password = 'Please enter a valid password.';
    }
    return errors;
  };

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    setFormErrors({});
    setError(null);

    try {
      const response = await axios.post(`${window.env.API_URL}/api/v1/auth/login`, formData);

      if (response.status === 200) {
        const { user, tokens } = response.data as {
          user: unknown;
          tokens: { access: { token: string }; refresh: { token: string } };
        };
        localStorage.setItem('accessToken', tokens.access.token);
        localStorage.setItem('refreshToken', tokens.refresh.token);
        localStorage.setItem('userDetails', JSON.stringify(user));
        setAuth(true);
        showSuccessToast('Login successful');
        navigate('/app/home');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err: any) {
      const msg = getErrorMessage(err, 'Login failed');
      setError(`Login error: ${msg}`);
      showErrorToast(msg);
      setFormData({
        email: '',
        password: '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!error) return undefined;
    const onKeyDown = () => setError(null);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f6f8fb] font-sans px-4 sm:px-6 py-6 sm:py-10">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-soft-lg border border-gray-100 relative overflow-hidden grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-center p-10 bg-gradient-to-br from-brand-light/70 via-white to-white border-r border-slate-100">
          <img
            src={logo}
            alt="Fincentive Logo"
            className="h-auto w-56 object-contain mb-6"
          />
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">
            Collect and manage VPAs faster
          </h2>
          <p className="mt-3 text-sm text-slate-600 max-w-sm">
            Optimized collection workflow with easy context selection and quick QR capture.
          </p>
        </div>

        <div className="p-6 sm:p-9 relative">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand to-brand-dark" />
          <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-brand/5 blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-brand/5 blur-2xl" />
          <div className="relative">
            <img
              src={logo}
              alt="Fincentive Logo"
              className="mx-auto lg:hidden mt-3 mb-7 h-auto w-44 object-contain"
            />

            {loading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand" />
              </div>
            ) : (
              <form className="space-y-4" onSubmit={handleLogin} noValidate>
                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full px-4 py-2.5 border rounded-lg ${
                      formErrors.email
                        ? 'border-red-500 focus:ring-red-500/30'
                        : 'border-gray-200 focus:ring-brand/30'
                    } shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand transition-all`}
                    placeholder="you@example.com"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-xs text-[#ff0000]">{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full px-4 py-2.5 border rounded-lg ${
                      formErrors.password
                        ? 'border-red-500 focus:ring-red-500/30'
                        : 'border-gray-200 focus:ring-brand/30'
                    } shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand transition-all`}
                    placeholder="********"
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-xs text-[#ff0000]">{formErrors.password}</p>
                  )}
                </div>

                <div className="pt-1">
                  <button
                    type="submit"
                    className="fc-btn-primary w-full"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            )}

            {error && (
              <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-100 animate-fade-in">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
